import type { DragEvent as ReactDragEvent } from 'react';

export const SALE_ASSIGN_DRAG_MARKER = 'application/x-sale-assign';
export const SALE_ORDER_TABLE_PREFIX = 'sale-order-table-';

export type SaleAssignDragPayload = {
  type: string;
  fromName?: string;
  name?: string;
  message?: string | null;
  id?: string;
  fromId?: string;
  lastActionType?: string;
  lastComment?: string | null;
  lastMessage?: string | null;
  queueId?: string;
  conversationId?: string;
  [key: string]: unknown;
};

let activeDrag: SaleAssignDragPayload | null = null;
const dragListeners = new Set<() => void>();

let selectedAssign: SaleAssignDragPayload | null = null;
const selectionListeners = new Set<() => void>();

export function getSaleOrderTableDroppableId(variantId: string) {
  return `${SALE_ORDER_TABLE_PREFIX}${variantId}`;
}

export function parseSaleOrderTableDroppableId(id: string | number | null | undefined) {
  if (typeof id !== 'string' || !id.startsWith(SALE_ORDER_TABLE_PREFIX)) return null;
  return id.slice(SALE_ORDER_TABLE_PREFIX.length);
}

export function getAssignPayloadKey(payload: SaleAssignDragPayload) {
  const type = payload.type ?? 'unknown';
  const id = String(
    payload.id ?? payload.queueId ?? payload.conversationId ?? payload.fromId ?? '',
  );
  return `${type}-${id}`;
}

export function isSameAssignPayload(
  a: SaleAssignDragPayload | null,
  b: SaleAssignDragPayload | null,
) {
  if (!a || !b) return false;
  return getAssignPayloadKey(a) === getAssignPayloadKey(b);
}

export function setSaleAssignDrag(payload: SaleAssignDragPayload | null) {
  activeDrag = payload;
  dragListeners.forEach((listener) => listener());
}

export function getSaleAssignDrag() {
  return activeDrag;
}

export function subscribeSaleAssignDrag(listener: () => void) {
  dragListeners.add(listener);
  return () => {
    dragListeners.delete(listener);
  };
}

export function setSelectedAssignPayload(payload: SaleAssignDragPayload | null) {
  selectedAssign = payload;
  selectionListeners.forEach((listener) => listener());
}

export function getSelectedAssignPayload() {
  return selectedAssign;
}

export function subscribeSelectedAssign(listener: () => void) {
  selectionListeners.add(listener);
  return () => {
    selectionListeners.delete(listener);
  };
}

export function toggleSelectedAssign(payload: SaleAssignDragPayload) {
  if (isSameAssignPayload(selectedAssign, payload)) {
    setSelectedAssignPayload(null);
    return;
  }
  setSelectedAssignPayload(payload);
}

export function clearSelectedAssign() {
  setSelectedAssignPayload(null);
}

export function isSaleAssignPayload(payload: Record<string, unknown>) {
  return (
    payload.type === 'facebook_comment' ||
    payload.type === 'queue_customer' ||
    payload.type === 'conversation' ||
    payload.lastActionType === 'COMMENT'
  );
}

export function getDragCustomerName(payload: Record<string, unknown>) {
  if (typeof payload.fromName === 'string' && payload.fromName.trim()) {
    return payload.fromName.trim();
  }
  if (typeof payload.name === 'string' && payload.name.trim()) {
    return payload.name.trim();
  }
  return 'Customer';
}

export function getDragPreviewMessage(payload: SaleAssignDragPayload) {
  const message =
    payload.message ?? payload.lastComment ?? payload.lastMessage ?? null;
  return typeof message === 'string' ? message : null;
}

export function setSaleAssignDragImage(
  event: ReactDragEvent,
  customerName: string,
  message?: string | null,
) {
  if (!event.dataTransfer) return;
  const preview = document.createElement('div');
  preview.style.cssText = [
    'position: fixed',
    'top: -1000px',
    'left: -1000px',
    'padding: 8px 12px',
    'background: #1877F2',
    'color: white',
    'border-radius: 10px',
    'font: 600 12px/1.3 system-ui, sans-serif',
    'max-width: 220px',
    'box-shadow: 0 8px 24px rgba(0,0,0,.18)',
    'pointer-events: none',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = customerName;
  preview.appendChild(title);

  if (message?.trim()) {
    const body = document.createElement('div');
    body.style.cssText =
      'margin-top:2px;font-weight:500;opacity:.9;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:196px';
    body.textContent = message.trim();
    preview.appendChild(body);
  }

  document.body.appendChild(preview);
  event.dataTransfer.setDragImage(preview, 16, 16);
  window.setTimeout(() => preview.remove(), 0);
}

export function isSaleAssignDragEvent(event: ReactDragEvent) {
  if (!event.dataTransfer) return false;
  return (
    event.dataTransfer.types.includes(SALE_ASSIGN_DRAG_MARKER) ||
    event.dataTransfer.types.includes('application/json')
  );
}

export function getPointerClientY(event: Event | null, deltaY = 0) {
  if (!event) return null;
  if ('clientY' in event && typeof event.clientY === 'number') {
    return event.clientY + deltaY;
  }
  if ('touches' in event) {
    const touchList = event.touches as TouchList;
    if (touchList?.[0]) {
      return touchList[0].clientY + deltaY;
    }
  }
  return null;
}
